// SGNL-191 — boot-time no-access page.
//
// Rendered when /api/whoami returns signal_access !== 'granted'. Replaces
// the SPA shell entirely (no hero, no nav, no KPI cards) so a Bounteous
// SSO user who hasn't been granted Signal access lands on a clean page
// instead of seeing the loading shell and a wall of 403s when /api/data
// fails. Also hides the global header / nav so there's nothing clickable
// that 403s.

import type { WhoAmI } from "../types";
import { esc } from "../lib/format";

const SIGNAL_APP_KEY = "signal";

function buildHtml(user: WhoAmI): string {
  const pending = user.signal_access === "pending";
  const headline = pending
    ? "Access request submitted"
    : "You don't have access to Signal yet";
  const body = pending
    ? `Your access request is awaiting approval from the Signal team. You'll receive an email once it's been reviewed.`
    : `Signal is Bounteous's internal AI-augmented Salesforce pipeline analytics. To use it, request access — a platform admin will review.`;
  const cta = pending
    ? `<button type="button" class="btn btn-secondary" disabled aria-disabled="true">Request submitted</button>`
    : `<button type="button" class="btn btn-primary" data-action="request-access" data-testid="request-access-btn">Request access</button>`;
  return `
    <main class="no-access" data-testid="no-access-page">
      <section class="no-access-card">
        <h1 class="no-access-title">${esc(headline)}</h1>
        <p class="no-access-user">Signed in as <strong>${esc(user.email)}</strong></p>
        <p class="no-access-body">${esc(body)}</p>
        <div class="no-access-actions">
          ${cta}
          <a class="no-access-signout" href="/logout">Sign out</a>
        </div>
        <p class="no-access-status" data-testid="no-access-status" role="status" aria-live="polite"></p>
      </section>
    </main>
  `;
}

function setStatus(host: HTMLElement, message: string, kind: "info" | "error" | "ok"): void {
  const el = host.querySelector<HTMLElement>('[data-testid="no-access-status"]');
  if (!el) return;
  el.textContent = message;
  el.className = `no-access-status no-access-status--${kind}`;
}

async function submitRequest(host: HTMLElement, button: HTMLButtonElement): Promise<void> {
  button.disabled = true;
  button.setAttribute("aria-disabled", "true");
  setStatus(host, "Submitting…", "info");
  try {
    const r = await fetch(`/api/apps/${encodeURIComponent(SIGNAL_APP_KEY)}/request-access`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    });
    if (r.ok || r.status === 201) {
      // Q-05 (PR #111 code review): symmetric with the static pending-
      // state CTA template — explicitly disable + aria-disabled here so
      // a future refactor that adds a `finally { btn.disabled = false }`
      // can't silently re-enable the submit button on the success path.
      button.textContent = "Request submitted";
      button.disabled = true;
      button.setAttribute("aria-disabled", "true");
      setStatus(host, "Thanks — your request is in the admin queue.", "ok");
      return;
    }
    if (r.status === 429) {
      setStatus(
        host,
        "Too many recent requests. Please try again in an hour.",
        "error",
      );
      // Allow retry once the limiter window rolls forward.
      button.disabled = false;
      button.removeAttribute("aria-disabled");
      return;
    }
    if (r.status === 409) {
      // User already has access (race between page load and click).
      setStatus(host, "You already have access — reload the page.", "info");
      return;
    }
    setStatus(host, `Request failed (HTTP ${r.status}). Try again or contact the Signal team.`, "error");
    button.disabled = false;
    button.removeAttribute("aria-disabled");
  } catch (e) {
    setStatus(host, `Network error: ${(e as Error).message}`, "error");
    button.disabled = false;
    button.removeAttribute("aria-disabled");
  }
}

/**
 * Render the no-access page in place of the SPA shell. Caller is
 * expected to also hide the global header/nav so the page reads as a
 * standalone landing rather than a broken sub-state of the SPA.
 */
export function renderNoAccessPage(host: HTMLElement, user: WhoAmI): void {
  host.innerHTML = buildHtml(user);
  const button = host.querySelector<HTMLButtonElement>('[data-action="request-access"]');
  if (button) {
    button.addEventListener("click", () => {
      void submitRequest(host, button);
    });
  }
}
